import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import type { ComponentType } from 'react';

/**
 * Phase 1: Panel Scope Registry 타입 정의
 *
 * 확장성 개선: Literal union 대신 string + helper 기반 접근법 사용
 * 200+ scope 관리 가능성 확보
 */

/**
 * Panel Scope Key (string 기반)
 *
 * 형식: `${service}/${domain}` 또는 `${service}/${domain}/${mode}`
 * 예시: 'dns/zone', 'dns/record/detail', 'iam/user/list'
 *
 * @example
 * 'dns/zone'           // service: dns, domain: zone
 * 'dns/record/detail'  // service: dns, domain: record, mode: detail
 * 'iam/user/list'      // service: iam, domain: user, mode: list
 */
export type PanelScopeKey = string;

/**
 * Bridge Mode: 레거시 연동 여부
 * - 'on': 레거시 시스템과 양방향 연동
 * - 'off': V2 전용 (레거시 우회)
 */
export type BridgeMode = 'on' | 'off';

/**
 * Panel Mode: 패널/독립 페이지 모드
 * - 'panel': 패널로 표시
 * - 'standalone': 독립 페이지로 표시
 */
export type PanelMode = 'panel' | 'standalone';

/**
 * View Mode: 목록/상세 구분
 * - 'list': 목록 뷰
 * - 'detail': 상세 뷰
 */
export type ViewMode = 'list' | 'detail';

/**
 * Panel Scope ID 구조
 *
 * Helper 함수의 입력으로 사용되며, 명시적인 구조를 제공합니다.
 */
export interface PanelScopeId {
  /**
   * 도메인/리소스 이름 (kebab-case)
   * @example 'zone', 'record', 'user', 'role'
   */
  domain: string;

  /**
   * 서비스 이름 (kebab-case)
   * @example 'dns', 'iam', 'vpc'
   */
  service: string;

  /**
   * 뷰 모드 (optional, 기본값: 'list')
   * @example 'list', 'detail'
   */
  mode?: ViewMode;
}

/**
 * GNB Panel 옵션 (Phase 7 연동 시 재사용)
 */
export interface GnbPanelOptions {
  /**
   * 패널 위치
   * - 'right': 우측 패널
   * - 'bottom': 하단 패널
   */
  position: 'right' | 'bottom';

  /**
   * 패널 크기
   * - 's': 작음
   * - 'm': 중간
   * - 'l': 큼
   */
  size: 's' | 'm' | 'l';
  // Phase 7에서 추가될 수 있는 옵션 대비
}

/**
 * 패널 링크 파라미터 (resolvePanelHref용)
 */
export interface PanelLinkResolveParams {
  /**
   * 패널 ID (단일 또는 다중)
   */
  panelId: string | string[];

  /**
   * Scope 키
   */
  scopeKey: PanelScopeKey;
}

/**
 * Panel Scope 설정
 */
export interface PanelScopeConfig {
  /**
   * Bridge 모드
   */
  bridgeMode: BridgeMode;

  /**
   * 기본 패널 모드
   */
  defaultPanelMode: PanelMode;

  /**
   * Panel 리소스 타입 (Phase 2+에서 PanelNavigation이 사용)
   * @example 'zone', 'record', 'user', 'role'
   */
  panelResource: string;

  /**
   * Scope 키 (고유 식별자)
   */
  scopeKey: PanelScopeKey;

  /**
   * 기본 GNB 옵션 (optional)
   */
  defaultGnbOptions?: GnbPanelOptions;

  /**
   * PanelRenderer가 사용할 패널 컴포넌트 맵 (서비스에서 주입)
   *
   * Record 타입으로 다중 컴포넌트를 resourceType별로 매핑합니다.
   * - 'default': Selection 기반 패널에서 사용 (List 화면)
   * - resourceType: 탭 패널에서 사용 (Detail 화면)
   *
   * @note 서비스 내에서 일반 import로 가져와서 직접 전달합니다.
   *       페이지 단위로 expose되므로 lazy 로딩 불필요.
   *
   * @example List 화면 (단일 컴포넌트)
   * ```tsx
   * import ZonePanel from '../ui/panel/ZonePanel';
   *
   * {
   *   panelComponent: { default: ZonePanel }
   * }
   * ```
   *
   * @example Detail 화면 (다중 컴포넌트)
   * ```tsx
   * import ZonePanel from '../ui/panel/ZonePanel';
   * import RecordTabPanel from '../ui/panel/RecordTabPanel';
   *
   * {
   *   panelComponent: {
   *     default: ZonePanel,
   *     record: RecordTabPanel,
   *   }
   * }
   * ```
   */
  panelComponent?: Record<string, ComponentType<any>>;

  /**
   * 패널 링크 주소를 생성하는 helper (선택)
   *
   * usePanelLinkRouter에서 embedded 컨텍스트에서의 router.push나
   * 기본 링크 동작에 사용됩니다.
   *
   * @note 없으면 서비스에서 각 href를 직접 조립해서 PanelLinkParams.href로 전달
   *
   * @example
   * ```tsx
   * resolvePanelHref: ({ scopeKey, panelId }) => `/dns/zones/${panelId}`
   * ```
   */
  resolvePanelHref?: (params: PanelLinkResolveParams) => string;
}

/**
 * Registry Store 상태 및 액션
 */
interface PanelScopeRegistryState {
  registry: Record<PanelScopeKey, PanelScopeConfig>;
}

interface PanelScopeRegistryActions {
  getScopeConfig: (scopeKey: PanelScopeKey) => PanelScopeConfig | undefined;
  registerScopes: (configs: PanelScopeConfig[]) => void;
}

export type PanelScopeRegistryStore = PanelScopeRegistryState & PanelScopeRegistryActions;

const STORE_NAME = 'PANEL_SCOPE_REGISTRY';

// 슬래시(/)를 구분자로 사용: service/domain 또는 service/domain/mode
// 예: 'vpc/subnet/list', 'vpc/routing-table/list', 'dns/zone'
const isValidScopeKey = (key: string) => /^[a-z0-9-]+\/[a-z0-9-]+(\/[a-z0-9-]+)?$/.test(key);

/**
 * Phase 1: 단일 상태/registry (config만 관리)
 *
 * 각 서비스가 독립적으로 scope를 등록할 수 있도록 분산 등록 패턴을 지원합니다.
 * registerScopes는 누적 병합(merge) 방식으로 동작하여 여러 서비스가 독립적으로 호출 가능합니다.
 * Phase 1에서는 Registry만 관리하며, PanelNavigation/SelectionChannel 연동은
 * Phase 2에서 구현됩니다.
 */
export const usePanelScopeRegistry = create<PanelScopeRegistryStore>()(
  devtools(
    (set, get) => ({
      registry: {} as Record<PanelScopeKey, PanelScopeConfig>,

      registerScopes: configs => {
        set(
          state => {
            const nextRegistry = { ...state.registry };

            configs.forEach(cfg => {
              if (!isValidScopeKey(cfg.scopeKey)) {
                console.warn(
                  `[PanelScopeRegistry] Invalid scopeKey: ${cfg.scopeKey}. Expected format "service/domain" or "service/domain/mode"`,
                );
                return;
              }

              if (nextRegistry[cfg.scopeKey]) {
                console.warn(
                  `[PanelScopeRegistry] Scope already registered: ${cfg.scopeKey}. Skipping duplicate.`,
                );
                return;
              }
              nextRegistry[cfg.scopeKey] = cfg;
            });

            return { registry: nextRegistry };
          },
          undefined,
          `${STORE_NAME}/registerScopes`,
        );
      },

      getScopeConfig: scopeKey => {
        return get().registry[scopeKey];
      },
    }),
    { name: STORE_NAME },
  ),
);
